import React from "react";
import GlobalNav from "../components/GlobalNav";
import Footer from "../components/Footer";
import { Quote, Disc3, Grid3x3, AudioWaveform, HelpCircle } from "lucide-react";
import SamplingHero from "../components/sampling/SamplingHero";
import { HistoryTracklist, NumberedSection, TechTimeline, HistoryFigure } from "../components/sampling/HistoryUI";
import TechniqueSection from "../components/sampling/TechniqueSection";
import { techniqueSections } from "../data/techniques-data";
import Playground from "../components/sampling/Playground";
import "./SamplingPage.css";

// Ported from Setloth/SampleBreakdown (Seth Weintraub -- the same Seth
// credited as co-host on the real "Music Is Healing Us Radio" episode
// found earlier in this project). Scoped under .sampling-page rather than
// the original's bare #root/h1/h2/p selectors, which would otherwise have
// applied a fixed 1126px width and dark theme to the ENTIRE site, not just
// this page. GlobalNav/Footer added for consistency with the rest of the
// site; the original's own in-page anchor nav (#history/#techniques/
// #examples-playground) stays, since it serves a different purpose --
// jumping between sections of this one long page -- not top-level site
// navigation.
export default function SamplingPage() {
  return (
    <div className="sampling-page">
      <GlobalNav activeHref="/sampling" ctaLabel="Join the collective" />

      <div className="sampling-page-inner">
        <section id="title" className="navbar">
          <h1>Sampling</h1>
          <nav className="site-nav">
            <a href="#history">History</a>
            <a href="#techniques">Techniques</a>
            <a href="#examples-playground">Playground</a>
          </nav>
          <section id="spacer"></section>
        </section>

        <section id="subtitle">
          <SamplingHero>
            <span className="site-eyebrow">Liner Notes</span>
            <p>This project was inspired by a few tools I use myself to learn about musical sampling.</p>
            <p><a href="https://whosampled.com">WhoSampled</a> is a website with a community-built catalog of songs and their samples, a useful tool to figure out what your favorite track or artist sampled, and what other songs have done the same. The fact that it is run off of community contributions means it is growing and changing all the time, allowing for professionals and beginners to get into the wide world of samples at any time. </p>
            <p><a href="https://www.youtube.com/tracklib">Tracklib</a> is a web platform that hosts samples for producers to use in their songs, and those samples - real songs - are legally cleared for use, making it really easy for anyone to use them in their work and then release it online. Tracklib also posts YouTube videos that break down samples, in a popular series, that has informed me a lot about the techniques producers use to transform samples into new beats. </p>
            <p>Both of these platforms have inspired me to want to create this project, to share my own view of sampling, and to present not only a textual presentation of its history and culture, but also audible and visual examples of the music and techniques that make it so special.</p>
          </SamplingHero>
        </section>

        <section className="text-section" id="history">
          <h1>History</h1>
          <p className="sub">The origin of sampling as a tool for music creation follows five key cornerstone developments and critical factors that shaped the environment that allowed for sampling culture to be born</p>
          <div className="content-divider" />

          <HistoryTracklist>
            <NumberedSection index={1} title="The Constraint">
              <p>In the twentieth century, the ability to capture, manipulate and mass-distribute sound itself allowed listeners to "hear performers they could not see and music they could not normally bring into their homes" (Katz, 12). Recording studio technology evolved into something more powerful than just putting music on a disc, producers in the studio could be the architects of layered soundscapes, creating laboratories of sound manipulation. An industry was built around this creation, putting a price on musical experimentation.</p>
              <br/>
              <blockquote className="pull-quote">
                <Quote size={16} />
                <div>
                  <p>Studio time, as well as musicians' fees, cost money, and technological delays could be frustrating and costly.</p>
                  <cite>— Horning, 118</cite>
                </div>
              </blockquote>

              <p>Studio technology was a major breakthrough for pioneering the musical scenes listeners had access to, but the economic barriers that surrounded these technologies meant that only select industry creators had access to the best equipment</p>

            </NumberedSection>
            <NumberedSection index={2} title="Turntablism">
              <p>In the summer of 1973, legendary disc jockey Clive Campbell, aka DJ Kool Herc, hosted the "Back to School Jam" in the Bronx. Campbell had made an ingenious discovery; if he connected two turntables via an audio mixing device, he could rewind one record silently while the other one played. By using the audio mixer to control which turntable was outputting, he could play one record, switch to the other, rewind, and 'loop' a section of the song over and over.</p>
              <iframe width="560" height="315" src="https://www.youtube.com/embed/FakAknt5KlI?si=cCQ7aZHj0K22-7LY" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>

              <p>With the addition of some big speakers, instead of having to get studio time to record their own music, these early hip-hop legends could just reuse the music they already had access to, and create their own energy outside of the studio</p>

            </NumberedSection>
            <NumberedSection index={3} title="Machines Arrived">
              <p>Turntables may have invented the workflow for sampling beats of records, but hip-hop didn't stop there. In the 1980s, other equipment like drum machines and audio samplers provided more sophisticated sound manipulation previously only accessible in a recording studio. </p>
              <HistoryFigure
                src="https://upload.wikimedia.org/wikipedia/commons/b/be/Roland_TR-808_drum_machine.jpg"
                alt="Roland TR-808 drum machine"
                sourceHref="https://commons.wikimedia.org/wiki/File:Roland_TR-808_drum_machine.jpg"
                sourceLabel="Wikimedia Commons"
              />
              <p>The Roland TR-808 and its iconic synthesized drum sounds provided customizable drum effects, like the iconic <a>808 Kick</a>, provided producers with palette to take their sampled loops even further. Hip-hop producers could take a sampled record and add their own percussive sounds over the music, rather than being limited to sampled drums first. </p>
              <HistoryFigure
                src="https://upload.wikimedia.org/wikipedia/commons/4/46/E-mu_SP-1200_%28111607sp1200%29.jpg"
                alt="E-mu SP-1200 Sampler"
                sourceHref="https://commons.wikimedia.org/wiki/File:E-mu_SP-1200_(111607sp1200).jpg"
                sourceLabel="Wikimedia Commons"
              />
              <p>The E-mu SP-1200 took things a step deeper, allowing producers to load sampled audio into different pad banks that could then be played back in a unique order, effectively 'chopping' up a song into different sonic moments. </p>

              <TechTimeline steps={[
                { id: "turntable", label: "Turntable", era: "1973", icon: <Disc3 size={26}/>, caption: "Looped a break by ear, live." },
                { id: "drum-machine", label: "Drum Machines", era: "1980s", icon: <Grid3x3 size={26}/>, caption: "Added programmable percussion over a sample." },
                { id: "sampler", label: "Samplers", era: "Late 1980s", icon: <AudioWaveform size={26}/>, caption: "Chopped and replayed audio in a new order." },
                { id: "unknown", label: "???", icon: <HelpCircle size={26}/>, caption: "Where's it going next?" },
              ]} />
            </NumberedSection>
          </HistoryTracklist>

        </section>

        <section className="text-section" id="techniques">
          <h1>Techniques</h1>
          <div className="content-divider" />
          <p> What sampling is truly, is a way to create from an already existing palette of music, something new and bold- but, ultimately uniquely personal. </p>
          <p> However, how the sampling is done, is vastly unlimited in approach. What I want to do now, is to highlight some common possible techniques by way of showcasing a selection of hip-hop and alternative musical sampling examples</p>

          {techniqueSections.map((t) => (
            <TechniqueSection key={t.id} id={t.id} title={t.title} blurb={t.blurb} studies={t.studies} />
          ))}
        </section>

        <section className="text-section" id="examples-playground">
          <h1>Playground</h1>
          <div className="content-divider" />
          <p>Pick a clip below and try chopping it yourself — drop a few pads, loop a section, break out of the loop by hitting a different pad.</p>
          <Playground />
        </section>

        <section className="text-section" id="closer">
          <h1>Closing Thoughts</h1>
          <div className="content-divider" />
          <p>So, what's next? First, I hope this project was able to be as interactive as informative, I hope anyone here learned a little, but also got to mess around a little and explore the world of musical sampling. I hope to continue working on this as a project for an online sampling tool, as well as a place to curate and store samples for future use. Like WhoSampled, I hope to have a community of samplers here, but I know that isn't easy. For now, I want to use this as a tool for myself and my creative processes. In the future, it would be amazing if I could use this tool as a portable music maker, or simply as a catalog of samples that I have used or wish to use.</p>
          <p>At the end of it, thank you for reading and playing along. I hope it was as enjoyable for you as it was for me to put this all together. <br/>Thank you.</p>
        </section>

        <section className="text-section" id="bibliography">
          <div className="content-divider"/>
          <p>
            AnkofunkTV1. (2012, January 30). Kool Herc & Grandwizard Theodor @ Viva Freestyle ft Storm [Video]. YouTube. https://www.youtube.com/watch?v=FakAknt5KlI E-MU SP-1200 (111607SP1200). (n.d.). https://commons.wikimedia.org/wiki/File:E-mu_SP-1200_(111607sp1200).jpg Horning, S. S. (2013). Chasing sound: Technology, Culture, and the Art of Studio Recording from Edison to the LP. JHU Press. Roland TR-808 drum machine. (n.d.). https://commons.wikimedia.org/wiki/File:Roland_TR-808_drum_machine.jpg
          </p>
        </section>

        <section id="author">
          <br/>
          <p>Seth Weintraub</p>
        </section>
      </div>

      <Footer />
    </div>
  );
}
